import _ from "lodash";
import client from "lib/apolloClient/client";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  createWeeklyPlannerTemplateMutation,
  copyWeeklyPlannerTemplateMutation,
  deleteWeeklyPlannerTemplateMutation,
  updateWeeklyPlannerTemplateMutation,
  createWeeklyPlannerSectionMutation,
  updateWeeklyPlannerSectionMutation,
  deleteWeeklyPlannerSectionMutation,
  moveWeeklyPlannerSectionContentMutation,
  reorderWeeklyPlannerSectionContentMutation,
  clearWeeklyPlannerSectionContentMutation,
  duplicateWeeklyPlannerSectionContentMutation,
  getWeeklyPlannerDownloadUrlMutation,
  updateWeeklyPlannerSectionItemsMutation,
  updateWeeklyPlannerSectionDescriptionMutation,
} from "./ClassPlannersMutations";
import asyncWrap from "Utils/asyncWrap";
import {
  getWeeklyPlannerTemplateByCourseQuery,
  getWeeklyPlannerTemplateSectionsQuery,
} from "./ClassPlannersQueries";
import {
  getWeeklyPlannerFragmentFromCache,
  getWeeklyPlannerTemplateFromCache,
  writeWeeklyPlannerTemplatesToCache,
  readWeeklyPlannerTemplateSectionFragment,
  writeWeeklyPlannerTemplateSectionFragmentToCache,
} from "./ClassPlannersGraphqlHelpers";
import { generateRandomId } from "Utils";
import update from "immutability-helper";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getFilteredExceptionCollaboratorList } from "./Utils";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import {
  goToRelativeRoute,
  downloadFromUrl,
  setProgress,
  removeProgressElement,
} from "modules/Services";
import moment from "moment";

export const NAME = "classPlanner";

export const UPDATE_CONFIGURATION_FILTER =
  "UPDATE_CONFIGURATION_FILTER" + " " + NAME;
export const INITIALIZE_CONFIGURATION_FILTER =
  "INITIALIZE_CONFIGURATION_FILTER" + " " + NAME;
export const SET_ASSIGNMENT_MODAL_DATA =
  "SET_ASSIGNMENT_MODAL_DATA" + " " + NAME;

export const updateConfigurationFilter = data => {
  return { type: UPDATE_CONFIGURATION_FILTER, data };
};

export const setAssignmentModalData = data => {
  return { type: SET_ASSIGNMENT_MODAL_DATA, data };
};

export const initializeConfigurationFilter = () => {
  return { type: INITIALIZE_CONFIGURATION_FILTER };
};

export const createWeeklyPlannerTemplate = ({
  title,
  startDate,
  endDate,
  defaultAccessState,
  courses,
  addedExceptionalCollaborators,
}) => {
  return async (dispatch, getState) => {
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );

    const academicYearId = getSelectedAcademicYearId(getState());
    const exceptionalCollaboratorFilters = {
      userAcademicYearId: academicYearId,
    };

    const refetchQueries = [
      {
        query: getWeeklyPlannerTemplateByCourseQuery,
        variables: {
          id: courseId,
          exceptionalCollaboratorFilters,
        },
      },
    ];

    let res;
    try {
      res = await client.mutate({
        mutation: createWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            title,
            startDate,
            endDate,
            defaultAccessState,
            courses,
            addedExceptionalCollaborators,
          },
          exceptionalCollaboratorFilters,
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      const plannerId = _.get(
        res,
        "data.planner.createWeeklyPlannerTemplate.id"
      );
      if (plannerId)
        dispatch(
          goToRelativeRoute({
            route: `${plannerId}/weekly`,
            type: "push",
          })
        );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};
export const copyWeeklyPlannerTemplate = ({
  id,
  title,
  startDate,
  endDate,
  itemTypes,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const academicYearId = getSelectedAcademicYearId(getState());
    const courseGradeFilters = {
      academicYearId,
    };
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );
    const courseDetails = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters,
    });
    const courseTitle = _.get(courseDetails, "title", "");
    const primaryGrade = _.get(courseDetails, "primaryGrade.id");

    const {
      id: userId,
      first_name = "",
      last_name = "",
      userEntityType = "Staff",
    } = userInfo;

    const exceptionalCollaboratorFilters = {
      userAcademicYearId: academicYearId,
    };
    const data = getWeeklyPlannerTemplateFromCache({
      id: courseId,
      exceptionalCollaboratorFilters,
    });

    const newClassPlannerTemplate = {
      id: generateRandomId(),
      title,
      startDate,
      endDate,
      createdAt: new Date().toISOString(),
      defaultAccessState: "VIEW",
      collaborators: {
        edges: [],
        __typename: "UnitPlanCollaboratorConnection",
      },
      createdBy: {
        id: userId,
        firstName: first_name,
        lastName: last_name,
        __typename: userEntityType,
      },
      courses: [
        {
          id: courseId,
          title: courseTitle,
          primaryGrade: {
            id: primaryGrade,
            __typename: "Grade",
          },
          __typename: "Course",
        },
      ],
      updatedAt: null,
      updatedBy: null,
      isSharedWithFamilies: false,
      isSharedWithStudents: false,
      exceptionalCollaborators: null,
      enabledSections: null,
      __typename: "WeeklyPlannerTemplate",
    };

    try {
      await client.mutate({
        mutation: copyWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
            title,
            startDate,
            endDate,
            itemTypes,
            courses: [courseId],
          },
          exceptionalCollaboratorFilters,
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            __typename: "PlannerMutations",
            copyWeeklyPlannerTemplate: newClassPlannerTemplate,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { copyWeeklyPlannerTemplate },
            },
          }
        ) => {
          const updatedData = update(data, {
            weeklyPlannerTemplates: {
              $unshift: [copyWeeklyPlannerTemplate],
            },
          });
          setTimeout(() =>
            writeWeeklyPlannerTemplatesToCache({
              variables: { id: courseId, exceptionalCollaboratorFilters },
              data: { node: updatedData },
            })
          );
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:duplicate_success_message",
          type: "success",
        })
      );
    } catch (e) {
      writeWeeklyPlannerTemplatesToCache({
        variables: { id: courseId, exceptionalCollaboratorFilters },
        data: { node: data },
      });

      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};
export const updateWeeklyPlannerTemplate = ({
  id,
  title,
  startDate,
  endDate,
}) => {
  return async (dispatch, getState) => {
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );

    const academicYearId = getSelectedAcademicYearId(getState());
    const exceptionalCollaboratorFilters = {
      userAcademicYearId: academicYearId,
    };

    const refetchQueries = [
      {
        query: getWeeklyPlannerTemplateByCourseQuery,
        variables: {
          id: courseId,
          exceptionalCollaboratorFilters,
        },
      },
    ];

    try {
      await client.mutate({
        mutation: updateWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
            title,
            startDate,
            endDate,
          },
          exceptionalCollaboratorFilters,
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:planner_successfully_updated",
          type: "success",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};

export const deleteWeeklyPlannerTemplate = ({ id }) => {
  return async (dispatch, getState) => {
    const courseId = _.get(
      getState(),
      "teacher.selected_class.selected_course",
      null
    );

    const academicYearId = getSelectedAcademicYearId(getState());
    const exceptionalCollaboratorFilters = {
      userAcademicYearId: academicYearId,
    };
    const data = getWeeklyPlannerTemplateFromCache({
      id: courseId,
      exceptionalCollaboratorFilters,
    });

    const updatedData = update(data, {
      weeklyPlannerTemplates: {
        $set: _.filter(data.weeklyPlannerTemplates, item => item.id !== id),
      },
    });

    writeWeeklyPlannerTemplatesToCache({
      variables: { id: courseId, exceptionalCollaboratorFilters },
      data: { node: updatedData },
    });

    try {
      await client.mutate({
        mutation: deleteWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
          },
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:planner_successfully_deleted",
          type: "success",
        })
      );
    } catch (e) {
      writeWeeklyPlannerTemplatesToCache({
        variables: { id: courseId, exceptionalCollaboratorFilters },
        data: { node: data },
      });
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};

export const shareWeeklyPlannerTemplate = ({
  id,
  courses = { added: [], removed: [] },
  addedExceptionalCollaborators = [],
  removedExceptionalCollaborators = [],
  defaultAccessState,
  enabledSections,
  isSharedWithFamilies,
}) => {
  return async (dispatch, getState) => {
    const academicYearId = getSelectedAcademicYearId(getState());

    const exceptionalCollaboratorFilters = {
      userAcademicYearId: academicYearId,
    };

    const getPlannerFragment = getWeeklyPlannerFragmentFromCache({
      id,
      variables: { exceptionalCollaboratorFilters },
    });

    let allCourses = _.map(_.get(getPlannerFragment, "courses", []), "id");
    allCourses = [...allCourses, ...courses.added];
    allCourses = allCourses.filter(course => !courses.removed.includes(course));

    // remove existingExceptionalCollaborators if the removed class is present in the course list of any exceptional collaborator.
    // The teacher should still exist if it exists in any other class which is not removed.
    const existingExceptionalCollaborators = _.get(
      getPlannerFragment,
      "exceptionalCollaborators.edges",
      []
    );

    const filteredCollaboratorListFromExistingException = _.reduce(
      existingExceptionalCollaborators,
      (result, item) => {
        const staffid = item.node.id;
        const staffCourses = _.map(item.node.courses, "id");
        const isCoursePresent = _.intersection(allCourses, staffCourses);
        if (isCoursePresent.length === 0) {
          result.push(staffid);
        }

        return result;
      },
      []
    );

    const filteredRemovedExceptionList = getFilteredExceptionCollaboratorList({
      exceptionalCollaborators: removedExceptionalCollaborators,
      isRemovingCollaborator: true,
      allCourses,
    });

    const finalRemovedExceptionalCollaborators = [
      ...filteredRemovedExceptionList,
      ...filteredCollaboratorListFromExistingException,
    ];

    const finalAddedExceptionalCollaborators = getFilteredExceptionCollaboratorList(
      {
        exceptionalCollaborators: addedExceptionalCollaborators,
        isRemovingCollaborator: false,
        allCourses,
      }
    );

    try {
      await client.mutate({
        mutation: updateWeeklyPlannerTemplateMutation,
        variables: {
          input: {
            id,
            courses,
            defaultAccessState,
            exceptionalCollaborators: {
              addedExceptionalCollaborators: finalAddedExceptionalCollaborators,
              removedExceptionalCollaborators: finalRemovedExceptionalCollaborators,
            },
            enabledSections,
            isSharedWithFamilies,
          },
          exceptionalCollaboratorFilters,
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:planner_successfully_shared",
          type: "success",
        })
      );
    } catch (e) {
      if (e.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      console.error(e);
    }
  };
};

export const createWeeklyPlannerSection = ({
  classPlannerId,
  startWeekDate,
  endWeekDate,
  showSuccessToast = true,
  sectionDetails = {},
}) => async (dispatch, getState) => {
  const {
    title,
    startDate,
    endDate,
    colorCode,
    startTime,
    endTime,
    recurrenceSetting,
    timezone,
    duration,
  } = sectionDetails;
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: createWeeklyPlannerSectionMutation,
      variables: {
        input: {
          title,
          templateId: classPlannerId,
          startDate,
          endDate,
          startTime,
          endTime,
          colorCode,
          timezone,
          recurrenceSetting,
          duration,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );
  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  } else if (showSuccessToast) {
    dispatch(
      setToastMsg({
        type: "success",
        msg: "toastMsgs:successfully_with_label",
        locale_params: [
          { key: "label", value: "toastMsgs:added", isPlainText: false },
        ],
      })
    );
  }
  return { error };
};

export const updateWeeklyPlannerSection = ({
  sectionDetails,
  actionFor,
  classPlannerId,
  startWeekDate,
  endWeekDate,
}) => async (dispatch, getState) => {
  const {
    id,
    title,
    startDate,
    endDate,
    colorCode,
    startTime,
    endTime,
    recurrenceSetting,
    timezone,
    duration,
  } = sectionDetails;

  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: updateWeeklyPlannerSectionMutation,
      variables: {
        input: {
          id,
          title,
          startDate,
          endDate,
          startTime,
          endTime,
          colorCode,
          actionFor,
          timezone,
          recurrenceSetting,
          duration,
        },
        collaboratorFilters,
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return { error };
};

export const deleteWeeklyPlannerSection = ({
  sectionId,
  classPlannerId,
  startWeekDate,
  endWeekDate,
  actionFor,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: deleteWeeklyPlannerSectionMutation,
      variables: {
        input: {
          id: sectionId,
          actionFor,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );
  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return { error };
};

export const updateWeeklyPlannerSectionItems = ({
  sectionId,
  editItems,
  description,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const [error] = await asyncWrap(
    client.mutate({
      mutation: updateWeeklyPlannerSectionItemsMutation,
      variables: {
        input: {
          id: sectionId,
          description,
          editItems,
        },
        collaboratorFilters,
      },
    })
  );
  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return {
    error,
  };
};

export const updateWeeklyPlannerSectionItemDescription = ({
  sectionId,
  description,
}) => async dispatch => {
  const [error] = await asyncWrap(
    client.mutate({
      mutation: updateWeeklyPlannerSectionDescriptionMutation,
      variables: {
        input: {
          id: sectionId,
          description,
        },
      },
    })
  );
  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return {
    error,
  };
};

export const reorderWeeklyPlannerSectionContent = ({
  classPlannerId,
  startWeekDate,
  endWeekDate,
  plannerSectionId,
  putAfterItemId,
  id,
  draggedDestinationIndex,
  draggedSourceIndex,
  sectionId,
  itemFilterId,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const sourceData = readWeeklyPlannerTemplateSectionFragment({
    id: sectionId,
    variables: { collaboratorFilters },
  });

  const filteredSectionItem = _.find(sourceData.items, item => {
    return _.get(item, "sectionItem.id") === itemFilterId;
  });

  const updatedSourceData = update(sourceData, {
    items: {
      $splice: [
        [draggedSourceIndex, 1],
        [draggedDestinationIndex, 0, filteredSectionItem],
      ],
    },
  });

  writeWeeklyPlannerTemplateSectionFragmentToCache({
    id: sectionId,
    data: updatedSourceData,
    variables: { collaboratorFilters },
  });

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: reorderWeeklyPlannerSectionContentMutation,
      variables: {
        input: {
          plannerSectionId,
          putAfterItemId,
          id,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return {
    error,
  };
};

export const moveWeeklyPlannerSectionContent = ({
  sourceSectionId,
  destinationSectionId,
  itemFilter,
  classPlannerId,
  startWeekDate,
  endWeekDate,
  putAfterItemId,
  draggedDestinationIndex,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const isDragAndDropContent = !_.isEmpty(itemFilter);
  if (isDragAndDropContent) {
    const sourceData = readWeeklyPlannerTemplateSectionFragment({
      id: sourceSectionId,
      variables: { collaboratorFilters },
    });

    let filteredSectionItem;
    const filteredSectionItems = _.filter(sourceData.items, item => {
      if (_.get(item, "sectionItem.id") == itemFilter?.id) {
        filteredSectionItem = { ...item, isItemDragged: true };
        return false;
      }
      return true;
    });

    const updatedSourceData = update(sourceData, {
      items: { $set: filteredSectionItems },
    });

    writeWeeklyPlannerTemplateSectionFragmentToCache({
      id: sourceSectionId,
      data: updatedSourceData,
      variables: { collaboratorFilters },
    });

    const destinationData = readWeeklyPlannerTemplateSectionFragment({
      id: destinationSectionId,
      variables: { collaboratorFilters },
    });

    if (filteredSectionItem) {
      const updatedDestinationData = update(destinationData, {
        items: {
          $splice: [[draggedDestinationIndex, 0, filteredSectionItem]],
        },
      });
      writeWeeklyPlannerTemplateSectionFragmentToCache({
        id: destinationSectionId,
        data: updatedDestinationData,
        variables: { collaboratorFilters },
      });
    }
  }

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];
  const [error] = await asyncWrap(
    client.mutate({
      mutation: moveWeeklyPlannerSectionContentMutation,
      variables: {
        input: {
          sourceSectionId,
          destinationSectionId,
          itemFilter,
          putAfterItemId,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return {
    error,
  };
};

export const clearWeeklyPlannerSectionContent = ({
  sectionIds,
  classPlannerId,
  startWeekDate,
  endWeekDate,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: clearWeeklyPlannerSectionContentMutation,
      variables: {
        input: {
          ids: sectionIds,
        },
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return {
    error,
  };
};

export const duplicateWeeklyPlannerSectionContent = ({
  input,
  classPlannerId,
  startWeekDate,
  endWeekDate,
}) => async (dispatch, getState) => {
  const academicYearId = getSelectedAcademicYearId(getState());
  const collaboratorFilters = {
    userAcademicYearId: academicYearId,
  };

  const refetchQueries = [
    {
      query: getWeeklyPlannerTemplateSectionsQuery,
      variables: {
        id: classPlannerId,
        filters: {
          startDate: moment(startWeekDate),
          endDate: moment(endWeekDate).endOf("day"),
        },
        collaboratorFilters,
      },
    },
  ];

  const [error] = await asyncWrap(
    client.mutate({
      mutation: duplicateWeeklyPlannerSectionContentMutation,
      variables: {
        input,
      },
      awaitRefetchQueries: true,
      refetchQueries,
    })
  );

  if (error) {
    dispatch(setToastMsg("toastMsgs:something_went_wrong"));
  }
  return {
    error,
  };
};

export const getWeeklyPlannerDownloadUrl = params => {
  const {
    input: { folderName, isLongRunningTask },
  } = params;
  const fileId = generateRandomId();
  /*Have added if (!isLongRunningTask) on multiple instances below since getWeeklyPlannerDownloadUrl will behave in two different ways. 
  If it's a longRunnerTask then we are sending a link so we have to show a different toast message and 
  if it's not a longRunnerTask then we have to show the progress bar as well as download the pdf from the given URL.*/
  const attachment = {
    id: fileId,
    type: "FILE",
    name: folderName,
    mimeType: "application/zip",
  };
  return async dispatch => {
    if (!isLongRunningTask)
      dispatch(
        setProgress({
          id: fileId,
          progress: 0,
          attachment,
          status: "SAVE",
          type: "FILE",
        })
      );

    try {
      const result = await client.mutate({
        mutation: getWeeklyPlannerDownloadUrlMutation,
        variables: params,
      });
      const { isSuccess, url, longRunningTaskCreated } = _.get(
        result,
        "data.planner.weeklyPlannerTemplateDownloadUrl"
      );
      if (!isLongRunningTask) dispatch(removeProgressElement({ id: fileId }));
      if (isSuccess) {
        if (!isLongRunningTask)
          dispatch(
            downloadFromUrl({
              attachment: {
                name: folderName,
                url,
                mimeType: "application/zip",
              },
              shouldAskForSave: true,
            })
          );
        else if (longRunningTaskCreated)
          dispatch(
            setToastMsg({
              msg: "toastMsgs:class_planner_download_success_msg",
              type: "success",
            })
          );
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } catch (error) {
      if (!isLongRunningTask) dispatch(removeProgressElement({ id: fileId }));
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

const REDUCER_HANDLERS = {
  [UPDATE_CONFIGURATION_FILTER]: (state, action) => {
    return update(state, { configurationFilters: { $set: action.data } });
  },

  [INITIALIZE_CONFIGURATION_FILTER]: (state, action) => {
    return update(state, {
      configurationFilters: { $set: initialState.configurationFilters },
      assignmentModalData: { $set: null },
    });
  },

  [SET_ASSIGNMENT_MODAL_DATA]: (state, action) => {
    return update(state, {
      assignmentModalData: { $set: action.data },
    });
  },
};

const initialState = {
  configurationFilters: [],
  assignmentModalData: null,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
